import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../../context/userContext";
import axios from "axios";
import FileList from "../FileList/fileList.component";
import Tooltip from "../Common/tooltip.component";
import { ReactComponent as RocketIcon } from "../../assets/icons/rocket_icon.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow_icon.svg";
import { ReactComponent as ChatIcon } from "../../assets/icons/chat_icon.svg";
import { ReactComponent as QrCodeIcon } from "../../assets/icons/qr_code_icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete_icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as NewSessionIcon } from "../../assets/icons/new_session_icon.svg";
import { ReactComponent as UpgradePlanIcon } from "../../assets/icons/upgrade_plan_icon.svg";
import { ReactComponent as UpgradeIcon } from "../../assets/icons/upgrade_icon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit_icon.svg";
import AddNewSession from "../NewSession/newSession.component";
import { Link } from "react-router-dom";
import SampleQuestionList from "../SampleQuestionList/sampleQuestionList.component";

function SessionListTab() {
  const {
    userData,
    setUserData,
    fetchDashboardData,
    scrollHeight,
    isContentLoading,
    setIsContentLoading,
  } = useUser();
  const {
    deleted_session_id,
    session_data,
    company_name,
    email,
    session_stats,
  } = userData;

  const [isStepZero, setIsStepZero] = useState(false);
  useEffect(() => {
    if (activeSessionId === null && (session_data ?? []).length === 0) {
      setTimeout(() => {
        setIsStepZero(true);
      }, 1000);
    }
  }, []);

  const [isStepOne, setIsStepOne] = useState(false);

  const [isUpgradePlanDialogOpen, setIsUpgradePlanDialogOpen] = useState(false);

  const [activeSessionId, setActiveSessionId] = useState(
    session_data && session_data.length > 0 ? session_data[0].session_id : null
  );

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);

  // const [sessionEditMode, setSessionEditMode] = useState(false);
  const [editedSessionName, setEditedSessionName] = useState("");

  const [qrDialog, setQrDialog] = useState(null);
  // const [showTooltip, setShowTooltip] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [showAddSessionComponent, setShowAddSessionComponent] = useState();

  // Fetch data when userData or session_data changes
  useEffect(() => {
    if (session_data && session_data.length > 0) {
      setActiveSessionId(session_data[session_data.length - 1].session_id);
      setShowAddSessionComponent(false);
      fetchData();
    }
  }, [userData, session_data]);

  useEffect(() => {
    if(session_data){
      setImageSrc(
        session_data.find((session) => session.session_id === activeSessionId)
          ?.logo_url
      );
    }
    if(session_data){
      setEditedSessionName(
      session_data.find((session) => session.session_id === activeSessionId)
        ?.qr_name
      );
    }
  }, [activeSessionId]);

  const handleSessionImageChange = async (e) => {
    const file = e.target.files[0];

    if (file) {
      const acceptedImageTypes = [
        // "image/gif",
        "image/jpeg",
        "image/png",
        "image/svg+xml",
      ];
      if (!acceptedImageTypes.includes(file.type)) {
        alert("Please select a valid image file.");
        return;
      }

      // Check if the file size is less than or equal to 2MB
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        alert("File size exceeds the limit of 2MB.");
        return;
      }

      // Use FileReader to convert the file to a data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file); // Ensure setSelectedFile is called here
    }

    try {
      setIsContentLoading(true);

      const logoFormData = new FormData();
      logoFormData.append("session_id", activeSessionId);
      logoFormData.append("logo", file); // Use 'file' directly here
      logoFormData.append("email", email);

      const logoResponse = await axios.post(
        "https://soliboat.solidaridadasia.com/edit_session_logo",
        logoFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userData?.token}`,
          },
          withCredentials: true,
        }
      );

      console.log("Logo response:", logoResponse.data.data[0].logo_url);

      const updatedSessionData = session_data.map((session) => {
        if (session.session_id === activeSessionId) {
          return {
            ...session,
            logo_url: logoResponse.data.data[0].logo_url,
          };
        }
        return session;
      });

      // Update the userData object with the modified session data
      setUserData((prevUserData) => ({
        ...prevUserData,
        session_data: updatedSessionData,
      }));
    } catch (error) {
      console.error("Error modifying session logo:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleSessionNameChange = (value) => {
    setEditedSessionName(value);
  };

  // Fetch uploaded files data
  const fetchData = async () => {
    try {
      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/uploaded_files",
        {
          email: email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      // console.log(response.data.data[0].uploaded_files)
      setUploadedFiles(response.data.data[0].uploaded_files);
    } catch (error) {
      console.error("Error fetching data:", error.code);
    }
  };

  // Filter files based on active session
  const filteredFiles = uploadedFiles
    .filter((file) => file.session_id === activeSessionId)
    .map((file) => ({
      files: file.files.map((fileInfo) => ({
        fileName: fileInfo.file_name,
        fileUri: fileInfo.file_url,
      })),
    }));

  // Open QR Code dialog with the URL of the active session
  const openQrDialog = () => {
    const activeSession = session_data.find(
      (session) => session.session_id === activeSessionId
    );

    if (activeSession) {
      setQrDialog(activeSession.qr_url);
    }
  };

  // Close QR Code dialog
  const closeQrDialog = () => {
    setQrDialog(null);
  };

  // Open file URL in a new tab
  const downloadFile = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  // Print QR Code
  const printQRCode = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body { margin: 0; }
            img { width:fit-content; height: fit-content; }
          </style>
        </head>
        <body>
          <img src="${qrDialog}" alt="QR Code" />
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  // Handle tab click to switch between sessions
  const handleTabClick = (sessionId) => {
    setActiveSessionId(sessionId);
    setShowAddSessionComponent(false);
  };

  const handleModifySessionData = async () => {
    setIsContentLoading(true);

    try {
      // Modify session name
      const nameFormData = new FormData();
      nameFormData.append("session_id", activeSessionId);
      nameFormData.append("qr_name", editedSessionName); // Change this to the edited session name
      nameFormData.append("email", email);

      const nameResponse = await axios.post(
        "https://soliboat.solidaridadasia.com/edit_session_name",
        nameFormData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData?.token}`,
          },
          withCredentials: true,
        }
      );

      console.log("Name update success:", nameResponse.data.data[0]);

      const updatedSessionData = session_data.map((session) => {
        if (session.session_id === activeSessionId) {
          return {
            ...session,
            qr_name: editedSessionName,
          };
        }
        return session;
      });

      // Update the userData object with the modified session data
      setUserData((prevUserData) => ({
        ...prevUserData,
        session_data: updatedSessionData,
      }));
    } catch (error) {
      console.error("Update failed:", error.message);
    } finally {
      setIsContentLoading(false);
    }
  };

  // Delete a session
  const handleDeleteSession = async (sessionID) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this session?"
    );

    if (!isConfirmed) {
      return;
    }

    setIsContentLoading(true);

    try {
      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/delete_session",
        {
          email: email,
          session_id: sessionID,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      alert(response.data.messages[0]);

      // Remove the deleted session from session_id array
      const updatedSessionData = userData.session_data.filter(
        (session) => session.session_id !== sessionID
      );

      // Update the userData context with the new session_id array
      setUserData((prevUserData) => ({
        ...prevUserData,
        session_data: updatedSessionData,
      }));

      if (updatedSessionData.length === 0) {
        setActiveSessionId(null);
      } else {
        setActiveSessionId(updatedSessionData[0].session_id);
      }

      await fetchData();
      await fetchDashboardData();
    } catch (error) {
      console.error("Error deleting session:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  // Handle the addition of a new session
  const handleAddNewSession = () => {
    if (session_stats?.remaining === 0) {
      setIsUpgradePlanDialogOpen(true);
    } else {
      setIsStepZero(false);
      setIsStepOne(false);
      setShowAddSessionComponent(true);
      setActiveSessionId(null);
    }
  };

  // const closeAddNewSession = () => {
  //   setShowAddSessionComponent(false);
  // };

  return (
    <>
      {/* Initial Loader Screen */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-neutral-50 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
          <div className="text-center text-sm lg:text-xl font-medium text-gray-500 mt-8 mx-8 lg:mx-0">
            Please wait while while we process your request{" "}
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
          </div>
        </div>
      )}

      {/* Session Limit Reached Dialog */}
      {isUpgradePlanDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.24)] z-50">
          <div className="fixed inset-0 bg-black opacity-50" />

          <div className="relative flex flex-col justify-center items-center p-4 w-[90%] md:w-2/3 lg:w-3/4 xl:w-1/3 bg-white rounded-xl">
            <div className="absolute top-3 right-3 flex rounded-full cursor-pointer hover:scale-[105%]">
              <CloseIcon
                className="w-8 h-8"
                onClick={() => setIsUpgradePlanDialogOpen(false)}
              />
            </div>
            <UpgradePlanIcon className="w-48 h-48" />
            <div className="flex flex-col justify-center items-center mb-8">
              <h2 className="mt-4 text-gray-900 text-2xl font-semibold">
                Upgrade Your Plan
              </h2>
              <p className="mt-4 mx-2 md:mx-4 text-center text-sm md:text-base text-gray-400">
                Your subscription plan allows for only{" "}
                {userData?.session_stats?.total} session(s). Please upgrade your
                plan to have additional sessions. If you require additional
                tokens, use the top-up option from the{" "}
                <span className="text-black font-medium">
                  "Account Details"
                </span>{" "}
                section.
              </p>
            </div>
            <div className="flex justify-center items-center w-4/5 mb-4 bg-primary/10 rounded">
              <button className="flex justify-center items-center w-4/5 bg-primary text-white m-4 px-2 py-2 rounded">
                <UpgradeIcon className="w-4 h-4 mr-2" fill="white" />
                <Link to="/#pricing">Upgrade Now</Link>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Section */}
      <div id="sessionListTab" className="mt-4">
        {/* Tab functionality section */}
        <div
          className={`flex items-center px-2 border border-neutral-200 ${
            activeSessionId === null && (session_data ?? []).length === 0
              ? "rounded-lg mb-3 md:shadow-session"
              : "rounded-t-lg mx-3 md:shadow-tabContainer"
          }`}
        >
          <div className="w-4/5 flex items-center scrollbar overflow-x-auto">
            {activeSessionId === null && (session_data ?? []).length === 0 ? (
              // Display a message when no sessions are created
              <div className="ml-2 md:ml-3">
                <p className="mt-2 text-black text-lg md:text-xl font-semibold">
                  Add a New Session
                </p>
                <p className="w-[90%] md:w-full mt-1 mb-2 mr-2 text-sm md:text-base text-neutral-600 font-medium">
                  Add a new session using the{" "}
                  <span className="inline-block md:hidden">plus</span>{" "}
                  <span className="hidden md:inline-block text-neutral-700 underline underline-offset-2">
                    Add New Session
                  </span>{" "}
                  button 👉
                </p>
              </div>
            ) : (
              // Display tabs for existing sessions
              session_data.map((sessionData, index) => (
                <div key={index} className="shrink-0 flex items-center">
                  <button
                    onClick={() => handleTabClick(sessionData.session_id)}
                    className={`flex justify-center items-center ${
                      activeSessionId === sessionData.session_id
                        ? "h-14 md:h-20 min-w-[64px] mx-1 mt-2 md:mt-3 mb-1 md:mb-0 px-[6px] md:px-2 pb-1 md:pb-[6px] rounded-t-xl rounded-b-xl md:rounded-b-none bg-white shadow-tabMobile md:shadow-tab cursor-pointer"
                        : "h-12 md:h-16 w-14 md:w-20 mt-3 mb-2 mx-1 rounded-xl bg-neutral-100 cursor-pointer"
                    } border-none focus:outline-none`}
                  >
                    <img
                      src={`${sessionData.logo_url}?${Date.now()}`} // Added a timestamp for cache-busting since the URL is same for the updated image.
                      alt={company_name}
                      className={`rounded-full ${
                        activeSessionId === sessionData.session_id
                          ? "h-9 md:h-14 w-9 md:w-14 mt-1 md:mt-0 mx-2 md:mx-0"
                          : "h-8 md:h-12 w-8 md:w-12"
                      } object-cover`}
                    />
                    {activeSessionId === sessionData.session_id && (
                      <p className="hidden md:block max-w-[144px] mx-2 font-medium line-clamp-1">
                        {sessionData.qr_name}
                      </p>
                    )}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Button to add a new session */}
          <div className="w-1/5 flex justify-end items-center">
            <button
              className={`${
                activeSessionId === null &&
                (session_data ?? []).length === 0 &&
                "pulse-button"
              } flex justify-center items-center ${
                showAddSessionComponent
                  ? "h-12 md:h-[72px] min-w-fit px-[14px] md:pb-[6px] rounded-t-xl bg-white shadow-addSessionButton cursor-pointer"
                  : "h-12 md:h-16 w-20 rounded-xl bg-neutral-100 cursor-pointer"
              } min-w-fit mt-3 mb-3 mx-1 md:p-2 bg-neutral-100 rounded-lg cursor-pointer hover:shadow-session focus:outline-none ${
                isStepOne ? "z-[100]" : ""
              }`}
              onClick={handleAddNewSession}
            >
              <AddIcon
                className="w-8 h-8 md:w-12 md:h-12 rotate-45"
                fill="grey"
              />
              <p className="hidden md:block mx-2 font-medium">
                Add New Session
              </p>
            </button>

            {isStepZero && deleted_session_id?.length === 0 && (
              <div
                className="absolute top-0 left-0 w-full bg-gray-800 bg-opacity-50 z-50"
                style={{ height: scrollHeight }}
              >
                {!isStepOne ? (
                  <div className="absolute top-[5%] left-[5%] md:top-[6%] md:left-[4%] lg:top-[10%] lg:left-[4%] xl:top-[10%] xl:left-[5%] flex items-center p-8 bg-white rounded-3xl">
                    <div>
                      <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                        WELCOME!
                      </p>
                      <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                        Let's start with a quick <br /> product tour
                      </h2>
                      <p className="text-sm">
                        We'll have you up and running <br /> in no time.
                      </p>
                      <button
                        className={`mt-4 mr-1 bg-gray-200 ${
                          !isStepOne ? "bg-opacity-100 cursor-not-allowed" : ""
                        } rounded-full`}
                      >
                        <ArrowIcon className="w-7 h-7 rotate-180" />
                      </button>
                      <button
                        className="mt-4 ml-1 bg-gray-200 bg-opacity-20 rounded-full"
                        onClick={() => setIsStepOne(true)}
                      >
                        <ArrowIcon className="w-7 h-7" />
                      </button>
                    </div>
                    <div>
                      <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                    </div>
                  </div>
                ) : (
                  <div className="absolute top-[5%] left-[5%] md:top-[6%] md:left-[4%] lg:top-[10%] lg:left-[4%] xl:top-[10%] xl:left-[5%] flex items-center p-8 bg-white rounded-3xl">
                    <div>
                      <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                        Let's begin!
                      </p>
                      <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                        Step 1: Add a New <br /> Session.
                      </h2>
                      <p className="text-sm">
                        Click on the{" "}
                        <span className="font-semibold">"Add New Session"</span>{" "}
                        button <br className="hidden md:block" /> to{" "}
                        <br className="block md:hidden" /> create a new session.
                      </p>
                      <button
                        className="mt-4 mr-1 bg-gray-200 bg-opacity-20 rounded-full"
                        onClick={() => setIsStepOne(false)}
                      >
                        <ArrowIcon className="w-7 h-7 rotate-180" />
                      </button>
                      <button
                        className={`mt-4 ml-1 bg-gray-200 ${
                          isStepOne ? "bg-opacity-100 cursor-not-allowed" : ""
                        } rounded-full`}
                      >
                        <ArrowIcon className="w-7 h-7" />
                      </button>
                    </div>
                    <div>
                      <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Initial Placeholder when no session is created */}
        {!showAddSessionComponent &&
          activeSessionId === null &&
          (session_data ?? []).length === 0 && (
            <>
              <div className="border border-neutral-100 mt-2 m-2" />
              <div className="rounded-lg">
                <div className="flex flex-col items-center justify-center py-8 bg-neutral-50 border border-neutral-300 rounded-xl">
                  <NewSessionIcon className="h-48 md:h-64 mb-2" />
                  <h1 className="text-slate-900 text-base md:text-xl font-semibold">
                    No session created yet...
                  </h1>
                  <p className="mx-2 my-1 text-center text-sm md:text-base text-neutral-700 leading-tight">
                    To create a new session & start using XplorEase click the{" "}
                    <span className="inline-block md:hidden text-primary underline underline-offset-2">
                      plus
                    </span>{" "}
                    <span className="hidden md:inline-block text-primary underline underline-offset-2">
                      Add New Session
                    </span>{" "}
                    button above.
                  </p>
                </div>
              </div>
            </>
          )}

        {/* Display content for the "add new session" & "active session" */}
        {showAddSessionComponent ? (
          <div className="p-4 bg-white border border-neutral-200 md:border-none md:shadow-session rounded-xl">
            {/* New Session Content Section */}
            <AddNewSession
              activeSessionId={activeSessionId}
              uploadedFiles={filteredFiles}
              fetchData={fetchData}
            />
          </div>
        ) : (
          activeSessionId &&
          session_data &&
          session_data.length > 0 && (
            // Display content when a session is selected
            <div className="p-4 bg-white border border-neutral-200 md:border-none md:shadow-session rounded-xl">
              <p className="md:mt-2 md:mb-4 text-neutral-900 text-lg md:text-xl xl:text-2xl font-semibold">
                Session Details
              </p>
              <div className="my-2 border-b border-neutral-200" />
              <div className="flex flex-col md:flex-row md:items-center w-full md:ml-2">
                <div className="md:w-3/5">
                  <div className="flex items-center mt-2 md:mt-4 w-full">
                    <label
                      htmlFor="fileInput"
                      className="w-1/5 md:w-auto relative flex justify-center items-center cursor-pointer"
                    >
                      <img
                        src={imageSrc}
                        alt={company_name}
                        className="rounded-full w-14 h-14 md:w-20 md:h-20 border"
                      />
                      <input
                        type="file"
                        id="fileInput"
                        accept=".tiff, .svg, .jpeg, .jpg, .png"
                        className="hidden"
                        onChange={handleSessionImageChange}
                        ref={fileInputRef}
                      />
                      <EditIcon
                        title="Edit Button"
                        className="absolute top-0 right-0 w-5 h-5 cursor-pointer rounded-full bg-gray-300 hover:bg-neutral-400 hover:scale-[102%]"
                      />
                    </label>
                    <div className="w-4/5 md:w-3/5 xl:w-2/5 flex flex-col items-end ml-3">
                      <div className="w-full mb-2 flex items-center">
                        <span className="w-1/2 flex items-center h-8 lg:h-10 text-sm md:text-base bg-gray-200 px-2 rounded-l-md text-gray-600 border border-neutral-200">
                          Session Name
                        </span>
                        <input
                          type="text"
                          value={editedSessionName}
                          onChange={(e) =>
                            handleSessionNameChange(e.target.value)
                          }
                          className="w-1/2 h-8 lg:h-10 px-2 text-base text-body-color rounded-r-md border border-neutral-200 placeholder-body-color outline-none focus:border-primary"
                        />
                      </div>
                      <button
                        className="h-8 lg:h-10 px-3 text-sm sm:text-base text-neutral-600 border border-neutral-400 rounded-md"
                        onClick={handleModifySessionData}
                      >
                        Update Name
                      </button>
                    </div>
                  </div>
                </div>
                <div className="md:hidden my-2 border-b border-neutral-200" />
                <div className="w-full md:w-2/5 mt-3 flex justify-between md:justify-end">
                  <div className="flex justify-between md:hidden">
                    <Link
                      to={`https://bot.xplorease.com/?session=${activeSessionId}`}
                      className="flex items-center mr-[6px] p-1 lg:px-[6px] lg:py-2 xl:p-2 lg:mr-2 bg-primary rounded-lg"
                    >
                      <ChatIcon className="w-4 md:w-5 h-4 md:h-5" />
                      <span className="ml-1 lg:ml-2 text-xs lg:text-base text-white hover:underline underline-offset-2">
                        Chat
                      </span>
                    </Link>
                    <button
                      className="flex items-center p-1 px-[6px] lg:px-[6px] lg:py-2 xl:p-2 lg:mr-2 bg-primary rounded-lg"
                      onClick={openQrDialog}
                    >
                      <QrCodeIcon className="w-4 md:w-5 h-4 md:h-5" />
                      <span className="ml-1 lg:ml-2 text-xs lg:text-base text-white hover:underline underline-offset-2">
                        View QR
                      </span>
                    </button>
                  </div>
                  <Link
                    to={`https://bot.xplorease.com/?session=${activeSessionId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:flex items-center p-1 lg:px-[6px] lg:py-2 xl:p-2 md:mr-1 lg:mr-2 bg-primary rounded-lg"
                  >
                    <ChatIcon className="w-4 md:w-5 h-4 md:h-5" />
                    <span className="ml-1 lg:ml-2 text-xs lg:text-base text-white hover:underline underline-offset-2">
                      Chat
                    </span>
                  </Link>
                  <Tooltip
                    className="md:hidden bottom-[105%] left-[-50%] md:left-[54%]"
                    width="120px"
                    content="View QR"
                  >
                    <button
                      className="group hidden md:flex items-center p-1 md:px-[6px] md:mr-1 lg:py-2 xl:p-2 lg:mr-2 bg-primary rounded-lg"
                      onClick={openQrDialog}
                    >
                      <QrCodeIcon className="w-5 md:w-6 h-5 md:h-6" />
                      <span className="ml-1 lg:ml-2 text-xs lg:text-base text-white hover:underline underline-offset-2">
                        View QR
                      </span>
                    </button>
                  </Tooltip>
                  <Tooltip
                    className="bottom-[105%] left-[-50%] md:left-[54%]"
                    width="120px"
                    content="Delete Session"
                  >
                    <button
                      className="group flex items-center p-1 md:px-[6px] lg:py-2 xl:p-2 lg:mr-3 border border-neutral-400 rounded-lg"
                      onClick={() => handleDeleteSession(activeSessionId)}
                    >
                      <DeleteIcon className="w-5 md:w-6 h-5 md:h-6 p-[3px] border border-neutral-700 rounded-full" />
                    </button>
                  </Tooltip>
                </div>
              </div>
              <FileList
                activeSessionId={activeSessionId}
                uploadedFiles={filteredFiles}
                fetchData={fetchData}
              />
              <SampleQuestionList activeSessionId={activeSessionId} />
            </div>
          )
        )}

        {/* QR Code Dialog */}
        {qrDialog && (
          <div className="fixed inset-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative bg-white p-4 rounded-lg md:shadow-md">
              <div
                className="absolute top-2 right-2 flex rounded-full cursor-pointer hover:scale-[105%]"
                onClick={closeQrDialog}
              >
                <CloseIcon className="w-7 h-7" title="Close" />
              </div>

              <div
                className="relative group"
                // onMouseEnter={() => setShowTooltip(true)}
                // onMouseLeave={() => setShowTooltip(false)}
              >
                {/*QR Code */}
                <img
                  src={qrDialog}
                  alt="QR Code"
                  className="w-48 h-48 mt-3 mx-3 group-hover:opacity-80 transition-opacity duration-300"
                />

                {/* Tooltip */}
                {/* {showTooltip && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black bg-opacity-75 text-white p-4 rounded">
                    <a
                      href={`https://bot.sn-ipl.com/?session=${activeSessionId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline tracking-tighter overflow-hidden"
                    >
                      Click to Explore!
                    </a>
                  </div>
                </div>
              )} */}
              </div>

              <div className="flex justify-between mx-3">
                <button
                  className="py-1 px-2 text-base font-semibold text-dark hover:underline hover:opacity-70 dark:text-white"
                  onClick={() => downloadFile(qrDialog)}
                >
                  Download
                </button>
                <button
                  className="py-1 px-2 text-base font-semibold text-dark hover:underline hover:opacity-70 dark:text-white"
                  onClick={printQRCode}
                >
                  Print
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SessionListTab;
